import * as globalUtils from 'global/common/globalUtils';
import * as model from './quickOrderModel';
import * as util from './quickOrderUtils';

export default class QuickOrder {
	constructor(wrap) {
		if (wrap) {
			this.wrap = wrap;
			this.footer = this.wrap.nextElementSibling;
			this.util = Object.assign({}, globalUtils, util);
			this.active = null;

			this.bindEvents();
		}
		this.subscribeEvents();
	}

	subscribeEvents() {
		window.od.events.subscribe(
			'productPriceTable_RowSelected',
			this.handlePriceTableRowSelected.bind(this),
		);
	}

	bindEvents() {
		this.wrap.addEventListener('focusout', e => this.triggerAction(e), false);
		this.wrap.addEventListener('click', e => this.triggerAction(e), true);
		this.wrap.addEventListener('submit', e => this.triggerAction(e), false);
	}

	handlePriceTableRowSelected({ articleNumber, syncId }) {
		const tableElement = document.querySelector(`[data-sync-id="${syncId}"]`);

		const input = tableElement
			?.closest(`.product-row__desc`)
			?.querySelector(
				`input[data-quantity="true"][data-article-number="${articleNumber}"]`,
			);

		if (input) {
			input.focus();
			setTimeout(() => input.blur(), 0);
		}
	}

	triggerAction(e) {
		let dispatchAction = null;

		if (e.type === 'submit') {
			e.preventDefault();
		}

		if (this.active === null) {
			switch (true) {
				case this.util.isFormSubmission(e):
					dispatchAction = 'doSubmitForm';
					break;
				case this.util.isDeleteButton(e):
					dispatchAction = 'doDeleteProduct';
					break;
				case this.util.isQuantityUpdate(e):
					dispatchAction = 'doUpdateQuantity';
					break;
				default:
			}
		}

		return dispatchAction && this[dispatchAction](e);
	}

	doSubmitForm(e) {
		const form = e.target.closest('form');
		const vikingSku = form.vikingSku.value.trim();
		const quantity = parseInt(form.quantity.value, 10) || 1;

		if (vikingSku.length) {
			this.active = form.parentNode;

			const formData = Object.assign({}, this.util.getFormData(form), {
				quantity,
				index: form.parentNode.getAttribute('data-index'),
			});

			this.skuSearch(formData);
		} else {
			this.util.resetForm(form);
		}
	}

	doUpdateQuantity(e) {
		const quantity = parseInt(e.target.value, 10);
		const rowData = this.util.getRowData(e.target);

		if (
			rowData &&
			!isNaN(quantity) &&
			quantity > 0 &&
			quantity !== rowData.quantity
		) {
			this.active = e.target.closest('[data-index]');
			const formData = Object.assign({}, rowData, { quantity });
			this.updateQuantity(formData);
		} else if (rowData && rowData.quantity) {
			e.target.value = rowData.quantity;
		}
	}

	doDeleteProduct(e) {
		const rowData = this.util.getRowData(e.target);

		if (rowData) {
			this.active = e.target.closest('[data-index]');

			this.deleteProduct(rowData);
		}
	}

	skuSearch(formData) {
		return model
			.searchSku(formData)
			.then(res => this.util.getNodeFromString(res.data))
			.then(row => this.updateSearchRowState(row))
			.then(row => this.updateDOM(row))
			.then(row => this.util.focusRowInput(row))
			.then(row => this.util.enableTooltip(row))
			.then(() => this.setFormState())
			.catch(() => window.location.reload(true));
	}

	updateQuantity(formData) {
		return model
			.updateQuantity(formData)
			.then(res => this.util.getNodeFromString(res.data))
			.then(row => this.updateDOM(row))
			.then(row => this.validateQuantity(row, formData.quantity))
			.then(row => this.util.enableTooltip(row))
			.catch(() => window.location.reload(true));
	}

	deleteProduct(formData) {
		return model
			.deleteProduct(formData)
			.then(res => this.util.getNodeFromString(res.data))
			.then(row => this.updateSearchRowState(row))
			.then(row => this.updateDOM(row))
			.then(() => this.setFormState())
			.catch(() => window.location.reload(true));
	}

	// DOM Manipulation

	updateSearchRowState(row) {
		const rowCount = this.util.getRowCount(this.wrap);
		const formsCount = this.util.getRowCount(this.wrap, 'search');
		const isProductRow = this.util.isRowType(row, 'product');

		if (formsCount < 2 && isProductRow) {
			this.appendSearchRow();
		} else if (rowCount > 3 && formsCount > 0 && !isProductRow) {
			this.removeSearchRow();
		}

		return row;
	}

	updateDOM(row) {
		this.prependRow(row);
		this.removeRow();
		this.util.clearErrorMessages('quickOrderPageAlert');

		return row;
	}

	validateQuantity(row, insertedQuantity) {
		const rowMinMax = this.util.getRowMinMax(row);
		if (
			(insertedQuantity < rowMinMax.minOrderQty && rowMinMax.element) ||
			(insertedQuantity > rowMinMax.maxOrderQty && rowMinMax.element)
		) {
			rowMinMax.element.classList.add(rowMinMax.errorClass);
		}
		return row;
	}

	appendSearchRow() {
		const newSearchForm = this.active.cloneNode(true);
		const newIndex = this.util.getRowCount(this.wrap);
		newSearchForm.setAttribute('data-index', newIndex);

		const form = newSearchForm.querySelector('form');
		this.util.resetForm(form);
		this.wrap.appendChild(newSearchForm);
	}

	removeSearchRow() {
		const lastElement = this.wrap.lastElementChild;

		if (
			lastElement.classList.contains('quick-order-search-row') &&
			lastElement !== this.active
		) {
			this.wrap.removeChild(lastElement);
		}
	}

	prependRow(newRow) {
		this.wrap.insertBefore(newRow, this.active);
	}

	removeRow() {
		this.wrap.removeChild(this.active);
		this.active = null;
	}

	setFormState() {
		const productRows = this.util.getRowCount(this.wrap, 'product');
		this.util.setFormButtonsState(this.footer, productRows);
	}
}
